/* iter55f62 — Mobile-first Writer's Notebook page.
 *
 * Route: `/notebook`
 * Auth : normal user session cookie (auto-cached on the phone after
 *        the first login — no repeated sign-in prompts).
 *
 * Layout is single-column, touch-friendly, and colour-matched to the
 * app's leather-and-gold theme. Renders on desktop too (no viewport
 * gating) so authors can preview from a laptop, but is optimised for
 * phone screens.
 *
 * Features
 * --------
 *   • First-open welcome popup: "Welcome back, <name>. Your tools for
 *     creating dreams." — dismissed on tap, remembered in localStorage.
 *   • Project chooser (pill row) — filters notes down to a single book,
 *     "Unfiled", or "All".
 *   • Add-note composer at the top; long-press-to-delete on each row.
 *   • iter55f69 — Voice memo button: MediaRecorder → Whisper via
 *     `/api/notebook/notes/voice` → note (marked `source: "voice"`).
 *   • Pagination (20 per page) with a "Load older" tail button.
 *   • Notes round-trip through `/api/notebook/*` — same data the
 *     desktop `NotebookPanel` sees, so the phone and the laptop stay
 *     in sync (SSE on the desktop side).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Loader2, Plus, Trash2, BookOpen, Sparkles, X, Mic, Square, HelpCircle, CornerDownRight } from 'lucide-react';
import { useAuth } from '@/lib/auth_context';
import { useVoiceRecorder } from '@/hooks/useVoiceRecorder';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const PAGE = 20;

/* ---------- palette (matches Scriptorium landing) ---------- */
const P = {
  leather:   '#8B6B60',
  paper:     '#f8f2e2',
  paperDeep: '#efe4c8',
  gold:      '#c6b491',
  goldDeep:  '#a99672',
  ink:       '#2a1f18',
  inkSoft:   '#6b5c4a',
  oxblood:   '#6A2E2E',
};

export default function MobileNotebookPage() {
  const { t } = useTranslation();
  const { user, loading } = useAuth();
  const [projects, setProjects] = useState([]);
  const [notes, setNotes] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [projectFilter, setProjectFilter] = useState('any');
  const [composerProject, setComposerProject] = useState('any');
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  // iter55f70 — AI Clarifier state. When set, we render an inline
  // reply composer under the parent note showing the LLM question.
  const [clarifier, setClarifier] = useState(null); // { parentId, question, reply }
  const [clarifierLoading, setClarifierLoading] = useState(null); // note id being asked
  const [clarifierSaving, setClarifierSaving] = useState(false);

  const load = useCallback(async (reset = true) => {
    if (!user) return;
    const params = { limit: PAGE, offset: reset ? 0 : offset };
    if (projectFilter !== 'any') params.project_id = projectFilter;
    try {
      const r = await axios.get(`${API}/notebook/notes`, { params });
      setTotal(r.data.total);
      setNotes(reset ? r.data.notes : [...notes, ...r.data.notes]);
      setOffset((reset ? 0 : offset) + r.data.notes.length);
    } catch (e) {
      toast.error(t('notebook.couldNotLoad'));
    }
  }, [user, projectFilter, offset, notes]);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/notebook/projects`).then((r) => setProjects(r.data)).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (user) load(true);
    // iter55f62 — reload when the filter changes; `load` is a stable arrow.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, projectFilter]);

  useEffect(() => {
    if (!user) return;
    const seen = window.localStorage.getItem('pw_notebook_welcome_seen');
    if (!seen) setShowWelcome(true);
  }, [user]);

  const dismissWelcome = () => {
    setShowWelcome(false);
    try { window.localStorage.setItem('pw_notebook_welcome_seen', '1'); } catch { /* blocked */ }
  };

  const addNote = async () => {
    if (!draft.trim() || busy) return;
    setBusy(true);
    try {
      const body = { content: draft.trim() };
      if (composerProject !== 'any' && composerProject !== 'unfiled') body.project_id = composerProject;
      const r = await axios.post(`${API}/notebook/notes`, body);
      setNotes([r.data, ...notes]);
      setTotal(total + 1);
      setDraft('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('notebook.couldNotSave'));
    } finally { setBusy(false); }
  };

  const deleteNote = async (id) => {
    if (!window.confirm(t('notebook.deleteConfirm'))) return;
    try {
      await axios.delete(`${API}/notebook/notes/${id}`);
      setNotes(notes.filter((n) => n.id !== id));
      setTotal(total - 1);
    } catch { toast.error(t('notebook.couldNotDelete')); }
  };

  /* iter55f69 — Voice memo → Whisper → new note.
   * MediaRecorder + upload machinery lives in `useVoiceRecorder`. We
   * only need to describe how the transcribed blob should be uploaded
   * (which requires component-level state: composer target, clarifier
   * linkage), and the hook handles start/stop/mimetype/cleanup. */
  const uploadVoiceNote = useCallback(async (blob) => {
    try {
      const fd = new FormData();
      // Give the backend a filename hint so it can sniff the ext.
      const ext = (blob.type.split('/')[1] || 'webm').split(';')[0];
      fd.append('audio', blob, `voice.${ext}`);
      if (composerProject !== 'any' && composerProject !== 'unfiled') {
        fd.append('project_id', composerProject);
      }
      // iter55f70 — If we're in the middle of answering a clarifier
      // question via voice, bundle the linkage.
      if (clarifier?.parentId) fd.append('parent_id', clarifier.parentId);
      if (clarifier?.question) fd.append('clarifier_question', clarifier.question);
      const r = await axios.post(`${API}/notebook/notes/voice`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setNotes((cur) => [r.data, ...cur]);
      setTotal((t) => t + 1);
      // Once the reply lands, the clarifier composer can close.
      if (clarifier?.parentId) setClarifier(null);
      toast.success(t('notebook.voiceTranscribed'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('notebook.couldNotTranscribe'));
    }
  }, [composerProject, clarifier]);

  const {
    recording, transcribing, elapsedMs,
    start: startRecording, stop: stopRecording,
  } = useVoiceRecorder({ onUpload: uploadVoiceNote });

  /* iter55f70 — AI Clarifier flow.
   *   askClarifier(note)   — hits /clarifier, opens the inline reply
   *                          composer with the LLM's follow-up.
   *   saveClarifierReply() — creates a linked child note carrying
   *                          `parent_id` + `clarifier_question`.
   *   The user can also reply by voice — `uploadVoiceNote` reads
   *   `clarifier` state and forwards those fields automatically. */
  const askClarifier = async (note) => {
    if (clarifierLoading) return;
    setClarifierLoading(note.id);
    try {
      const r = await axios.post(
        `${API}/notebook/notes/${note.id}/clarifier`, {},
        { withCredentials: true },
      );
      setClarifier({ parentId: note.id, question: r.data.question, reply: '' });
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('notebook.couldNotAsk'));
    } finally { setClarifierLoading(null); }
  };

  const saveClarifierReply = async () => {
    if (!clarifier || !clarifier.reply.trim() || clarifierSaving) return;
    setClarifierSaving(true);
    try {
      const body = {
        content:            clarifier.reply.trim(),
        parent_id:          clarifier.parentId,
        clarifier_question: clarifier.question,
      };
      if (composerProject !== 'any' && composerProject !== 'unfiled') {
        body.project_id = composerProject;
      }
      const r = await axios.post(`${API}/notebook/notes`, body);
      setNotes((cur) => [r.data, ...cur]);
      setTotal((t) => t + 1);
      setClarifier(null);
      toast.success(t('notebook.followUpSaved'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('notebook.couldNotSaveFollowUp'));
    } finally { setClarifierSaving(false); }
  };

  useEffect(() => () => {
    // Voice recorder cleanup is handled inside `useVoiceRecorder`.
    // Kept as a no-op cleanup slot in case future teardown lives here.
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center" style={{ backgroundColor: P.paper }}>
        <Loader2 className="h-6 w-6 animate-spin" style={{ color: P.leather }} />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace state={{ from: '/notebook' }} />;

  const projectTitle = (pid) => {
    if (!pid) return t('notebook.unfiled');
    return projects.find((p) => p.id === pid)?.title || t('notebook.untitled');
  };

  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: P.paper, color: P.ink, fontFamily: 'Georgia, serif' }}
      data-testid="mobile-notebook-page"
    >
      {/* iter55f74 — Mobile-first shell.
        *  • `max-w-lg` (512 px) + `mx-auto` keeps the layout in a phone-
        *    shaped column even when viewed on a wider laptop viewport,
        *    so text lines are readable and buttons stay thumb-reachable.
        *  • Base font bumped to 15/16 px for legibility on 360-414 px
        *    handsets; meta rows sit at 12 px minimum. All buttons hit
        *    the 44 px touch-target guideline via py-2/py-2.5 + gap.
        *  • `break-words` + `overflow-wrap: anywhere` prevent long URLs
        *    or unbroken tokens (e.g. a pasted UUID) from bursting the
        *    card and forcing horizontal scroll. */}
      <div className="mx-auto max-w-lg" data-testid="nb-mobile-shell">
      {/* Header — leather bar with gold trim */}
      <header
        className="sticky top-0 z-10 flex items-center justify-between gap-2 px-4 py-3 shadow"
        style={{ backgroundColor: P.leather, color: P.gold, borderBottom: `2px solid ${P.gold}` }}
      >
        <div className="flex min-w-0 items-center gap-2">
          <BookOpen className="h-5 w-5 shrink-0" />
          <div className="truncate text-[16px] font-semibold tracking-wider">{t('notebook.title')}</div>
        </div>
        <div className="max-w-[45%] truncate text-[12px] uppercase tracking-wider opacity-80">
          {user.name || user.email}
        </div>
      </header>

      {/* Project filter pills */}
      <div className="px-3 py-2 overflow-x-auto">
        <div className="flex items-center gap-1.5 whitespace-nowrap">
          {[
            { id: 'any',     label: t('notebook.filterAll') },
            { id: 'unfiled', label: t('notebook.filterUnfiled') },
            ...projects.map((p) => ({ id: p.id, label: p.title })),
          ].map((opt) => (
            <button
              key={opt.id}
              onClick={() => setProjectFilter(opt.id)}
              data-testid={`nb-filter-${opt.id}`}
              className="rounded-full border px-3.5 py-1.5 text-[13px] font-semibold transition"
              style={{
                borderColor: projectFilter === opt.id ? P.leather : `${P.goldDeep}55`,
                backgroundColor: projectFilter === opt.id ? P.leather : P.paperDeep,
                color: projectFilter === opt.id ? P.gold : P.inkSoft,
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Composer */}
      <div className="mx-3 mt-1 mb-3 rounded-md border p-3 shadow-sm" style={{ borderColor: `${P.gold}88`, backgroundColor: P.paperDeep }}>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={t('notebook.composerPlaceholderSpark')}
          data-testid="nb-composer-textarea"
          rows={4}
          className="w-full resize-none rounded-sm border bg-transparent p-3 text-[15px] leading-relaxed outline-none"
          style={{ borderColor: `${P.goldDeep}55`, color: P.ink, fontFamily: 'Georgia, serif' }}
        />
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
          <select
            value={composerProject}
            onChange={(e) => setComposerProject(e.target.value)}
            data-testid="nb-composer-project"
            className="min-h-[40px] rounded-sm border bg-transparent px-2 py-1 text-[13px]"
            style={{ borderColor: `${P.goldDeep}55`, color: P.inkSoft, minWidth: '110px' }}
          >
            <option value="any">{t('notebook.unfiled')}</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
          </select>
          <div className="flex flex-wrap items-center gap-2">
            {/* iter55f69 — Voice memo → Whisper transcription */}
            {recording ? (
              <button
                onClick={stopRecording}
                data-testid="nb-voice-stop"
                className="inline-flex min-h-[40px] items-center gap-1.5 rounded-sm px-4 py-2 text-[13px] font-semibold text-white animate-pulse"
                style={{ backgroundColor: P.oxblood }}
              >
                <Square className="h-3.5 w-3.5" fill="currentColor" />
                {t('notebook.stop')} · {(elapsedMs / 1000).toFixed(1)}s
              </button>
            ) : (
              <button
                onClick={startRecording}
                disabled={transcribing || busy}
                data-testid="nb-voice-record"
                title={t('notebook.recordTitle')}
                aria-label={t('notebook.recordTitle')}
                className="inline-flex min-h-[40px] items-center gap-1.5 rounded-sm border px-4 py-2 text-[13px] font-semibold disabled:opacity-50"
                style={{
                  borderColor: `${P.leather}88`,
                  color: transcribing ? P.inkSoft : P.leather,
                  backgroundColor: P.paperDeep,
                }}
              >
                {transcribing
                  ? <><Loader2 className="h-4 w-4 animate-spin" /> {t('notebook.transcribing')}</>
                  : <><Mic className="h-4 w-4" /> {t('notebook.record')}</>}
              </button>
            )}
            <button
              onClick={addNote}
              disabled={!draft.trim() || busy || recording || transcribing}
              data-testid="nb-composer-save"
              className="inline-flex min-h-[40px] items-center gap-1.5 rounded-sm px-4 py-2 text-[13px] font-semibold disabled:opacity-50"
              style={{ backgroundColor: P.leather, color: P.gold }}
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              {t('notebook.saveNote')}
            </button>
          </div>
        </div>
      </div>

      {/* Notes list */}
      <ul className="px-3 pb-24 space-y-2.5" data-testid="nb-notes-list">
        {notes.length === 0 && (
          <li className="rounded-md border border-dashed p-6 text-center text-[13px]"
              style={{ borderColor: `${P.goldDeep}55`, color: P.inkSoft }}
              data-testid="nb-empty">
            {t('notebook.emptyMobile')}
          </li>
        )}
        {notes.map((n) => (
          <li
            key={n.id}
            data-testid={`nb-note-${n.id}`}
            className="rounded-md border p-3 shadow-sm"
            style={{
              borderColor: `${P.goldDeep}44`,
              backgroundColor: '#fbf6e6',
              // iter55f70 — Nest child (reply) notes visually so the
              // note-pair reads as a threaded exchange.
              marginLeft: n.parent_id ? '1rem' : undefined,
              borderLeft: n.parent_id ? `3px solid ${P.gold}` : undefined,
            }}
          >
            {n.clarifier_question && (
              <div
                className="mb-2 rounded-sm px-2 py-1.5 text-[12.5px] italic"
                style={{
                  backgroundColor: `${P.gold}18`,
                  color: P.goldDeep,
                  border: `1px dashed ${P.gold}88`,
                  overflowWrap: 'anywhere',
                }}
                data-testid={`nb-clarifier-question-${n.id}`}
              >
                <span className="mr-1 font-semibold not-italic" style={{ color: P.leather }}>
                  {t('notebook.asked')}
                </span>
                {n.clarifier_question}
              </div>
            )}
            <div
              className="whitespace-pre-wrap break-words text-[15px] leading-relaxed"
              style={{ overflowWrap: 'anywhere' }}
            >
              {n.content}
            </div>
            <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[12px]" style={{ color: P.inkSoft }}>
              <span className="inline-flex min-w-0 items-center gap-1">
                {n.source === 'voice' && (
                  <Mic
                    className="h-3.5 w-3.5 shrink-0"
                    style={{ color: P.leather }}
                    aria-label={t('notebook.voiceMemoLabel')}
                    data-testid={`nb-voice-marker-${n.id}`}
                  />
                )}
                {n.parent_id && (
                  <CornerDownRight
                    className="h-3.5 w-3.5 shrink-0"
                    style={{ color: P.goldDeep }}
                    aria-label={t('notebook.followUpLabel')}
                    data-testid={`nb-clarifier-marker-${n.id}`}
                  />
                )}
                <span className="truncate">
                  {projectTitle(n.project_id)} · {new Date(n.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                </span>
              </span>
              <div className="flex items-center gap-2">
                {!n.parent_id && (
                  <button
                    onClick={() => askClarifier(n)}
                    disabled={clarifierLoading === n.id || !!clarifier}
                    data-testid={`nb-clarifier-ask-${n.id}`}
                    aria-label={t('notebook.askTitle')}
                    title={t('notebook.askTitle')}
                    className="inline-flex min-h-[36px] items-center gap-1 rounded-sm px-2 py-1.5 text-[12px] font-semibold disabled:opacity-40"
                    style={{ color: P.leather }}
                  >
                    {clarifierLoading === n.id
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <HelpCircle className="h-3.5 w-3.5" />}
                    {t('notebook.ask')}
                  </button>
                )}
                <button
                  onClick={() => deleteNote(n.id)}
                  data-testid={`nb-delete-${n.id}`}
                  aria-label={t('notebook.deleteNote')}
                  className="inline-flex min-h-[36px] items-center gap-1 rounded-sm px-2 py-1.5 text-[12px] font-semibold"
                  style={{ color: P.oxblood }}
                >
                  <Trash2 className="h-3.5 w-3.5" /> {t('notebook.delete')}
                </button>
              </div>
            </div>

            {/* Inline clarifier reply composer — appears under the
                parent note the writer chose to expand. */}
            {clarifier?.parentId === n.id && (
              <div
                className="mt-3 rounded-sm border p-3"
                style={{ borderColor: `${P.gold}88`, backgroundColor: `${P.gold}14` }}
                data-testid={`nb-clarifier-composer-${n.id}`}
              >
                <div className="mb-1.5 text-[12.5px] font-semibold" style={{ color: P.leather }}>
                  {t('notebook.assistantAsks')}
                </div>
                <div
                  className="mb-2 text-[14px] italic"
                  style={{ color: P.ink, overflowWrap: 'anywhere' }}
                >
                  {clarifier.question}
                </div>
                <textarea
                  value={clarifier.reply}
                  onChange={(e) => setClarifier((c) => c ? { ...c, reply: e.target.value } : c)}
                  placeholder={t('notebook.replyPlaceholder')}
                  data-testid={`nb-clarifier-textarea-${n.id}`}
                  rows={3}
                  className="w-full resize-none rounded-sm border bg-transparent p-2.5 text-[14px] leading-relaxed outline-none"
                  style={{ borderColor: `${P.goldDeep}55`, color: P.ink, fontFamily: 'Georgia, serif' }}
                />
                <div className="mt-2 flex flex-wrap items-center justify-end gap-2">
                  <button
                    onClick={() => setClarifier(null)}
                    data-testid={`nb-clarifier-cancel-${n.id}`}
                    className="min-h-[38px] rounded-sm px-3 py-2 text-[12.5px] font-semibold"
                    style={{ color: P.inkSoft }}
                  >
                    {t('notebook.cancel')}
                  </button>
                  {!recording ? (
                    <button
                      onClick={startRecording}
                      disabled={transcribing}
                      data-testid={`nb-clarifier-voice-${n.id}`}
                      title={t('notebook.answerByVoice')}
                      className="inline-flex min-h-[38px] items-center gap-1 rounded-sm border px-3 py-2 text-[12.5px] font-semibold disabled:opacity-50"
                      style={{ borderColor: `${P.leather}88`, color: P.leather, backgroundColor: P.paperDeep }}
                    >
                      {transcribing
                        ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> {t('notebook.transcribing')}</>
                        : <><Mic className="h-3.5 w-3.5" /> {t('notebook.voice')}</>}
                    </button>
                  ) : (
                    <button
                      onClick={stopRecording}
                      data-testid={`nb-clarifier-voice-stop-${n.id}`}
                      className="inline-flex min-h-[38px] items-center gap-1 rounded-sm px-3 py-2 text-[12.5px] font-semibold text-white animate-pulse"
                      style={{ backgroundColor: P.oxblood }}
                    >
                      <Square className="h-3.5 w-3.5" fill="currentColor" />
                      {t('notebook.stop')} · {(elapsedMs / 1000).toFixed(1)}s
                    </button>
                  )}
                  <button
                    onClick={saveClarifierReply}
                    disabled={!clarifier.reply.trim() || clarifierSaving}
                    data-testid={`nb-clarifier-save-${n.id}`}
                    className="inline-flex min-h-[38px] items-center gap-1 rounded-sm px-3 py-2 text-[12.5px] font-semibold disabled:opacity-50"
                    style={{ backgroundColor: P.leather, color: P.gold }}
                  >
                    {clarifierSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                    {t('notebook.saveReply')}
                  </button>
                </div>
              </div>
            )}
          </li>
        ))}
        {notes.length < total && (
          <li>
            <button
              onClick={() => load(false)}
              data-testid="nb-load-more"
              className="w-full rounded-md border py-3 text-[13.5px] font-semibold"
              style={{ borderColor: `${P.goldDeep}55`, color: P.leather, backgroundColor: P.paperDeep }}
            >
              {t('notebook.loadOlderMobile', { count: total - notes.length })}
            </button>
          </li>
        )}
      </ul>
      </div>

      {/* Welcome popup */}
      {showWelcome && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-6"
          style={{ backgroundColor: 'rgba(42, 31, 24, 0.55)' }}
          onClick={dismissWelcome}
          data-testid="nb-welcome-modal"
        >
          <div
            className="relative max-w-sm rounded-lg border p-6 shadow-2xl text-center"
            style={{ backgroundColor: P.paper, borderColor: P.gold }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={dismissWelcome}
              aria-label={t('notebook.closeWelcome')}
              className="absolute right-3 top-3 rounded-full p-1"
              style={{ color: P.inkSoft }}
              data-testid="nb-welcome-close"
            >
              <X className="h-4 w-4" />
            </button>
            <Sparkles className="mx-auto h-7 w-7" style={{ color: P.goldDeep }} />
            <div className="mt-2 text-[12px] uppercase tracking-widest" style={{ color: P.oxblood }}>
              The Loom
            </div>
            <div className="mt-1 text-[20px] font-semibold" style={{ fontFamily: 'Georgia, serif' }}>
              {t('notebook.welcomeTitle', { name: user.name || (user.email && user.email.split('@')[0]) || t('notebook.welcomeFriend') })}
            </div>
            <div className="mt-2 text-[14px] italic" style={{ color: P.inkSoft }}>
              {t('notebook.welcomeSubtitle')}
            </div>
            <button
              onClick={dismissWelcome}
              data-testid="nb-welcome-continue"
              className="mt-5 inline-flex min-h-[44px] items-center gap-1.5 rounded-sm px-5 py-2.5 text-[14px] font-semibold"
              style={{ backgroundColor: P.leather, color: P.gold }}
            >
              {t('notebook.openMyNotebook')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
